  
import { useParams } from "react-router-dom";
import { Dynamic } from "../../../../../controllers/conductor";
import { SameDayLguController } from "../../../../../controllers/forms/sameday/LGU";
import { getController } from "../../../../../controllers/getter";
import { MainWindow } from "../../../MainWindow";

import "../../../../../styles/forms.css";
import { Button, IconButton } from "../../../../widgets/buttons";
import { getMonthName, parseDateWithTime } from "../../../../../misc/date";
import { ScrollingTable } from "../../../../widgets/table";

import React, { useEffect, useRef, useState } from "react";
import { Checkbox, FormTableInput } from "../../../../widgets/input";
import { Blocker, HorizontalGrid } from "../../../../widgets/misc";
import { AlertDrawer } from "../../../../sections/sidebar/AlertDrawer";
import icons from "../../../../../assets/icons";
import { MonthYearSelector } from "../../../../widgets/date_selector";
import { LoadingAnimation } from "../../../../widgets/loading";
import Client from "../../../../../Client";
import { SubmitButtonGroup } from "../../../../widgets/form";
import { pluralize } from "../../../../../misc/object";


function SameDayLGU({ args: { type } }) {

    const params = useParams();

    const ref = {
        lgu: params.lguid,
        month: Number(params.month),
        year: Number(params.year)
    };

    if(type === "submission") ref.submissionId = params.submissionid;

    const controller = getController(SameDayLguController, { type, ref });
    const { notifiers, status } = controller;
    
    return (<MainWindow currentTab="forms" subtab="sameday">
        <Dynamic conductor={[ notifiers.ready, notifiers.unauthorized ]} child={()=>
            status.unauthorized ? <Unauthorized /> : status.ready ? <Main controller={controller} /> : <LoadingWindow />
        } />
    </MainWindow>);
}

function Unauthorized() {
    return <div>Unauthorized</div>;
}

function LoadingWindow() {
    return (<center><LoadingAnimation /> Loading...</center>);
}


function Main({ controller }) {
    return <div className="form-main">
        <div className="alert-drawer-wrapper"><AlertDrawer controller={controller.notifiers.alert} /></div>
        <Header controller={controller} />
        <FormSection controller={controller}/>
        <Footer controller={controller} />
    </div>;
}


function Header({ controller }) {
    const { type, notifiers, status, canApprove } = controller;

    const typeLabel = type === "draft" ? "DRAFT" : "SUBMISSION";
    const onDateChange = ({ month, year }) => controller.changeDate(month, year);

    return <Dynamic conductor={notifiers.reloaded} child={()=>{
        const data = controller.data;
        const lguName = data.lgu.name;
        const province = data.lgu.province.name;
        const date = getMonthName(data.month) + " " + data.year;
        const author = data.author ? 
            (data.author.id === Client.account.id ? "YOU" : data.author.fullname) : 
            "Unknown";
        const approvals = data.approvals;

        const lastUpdatedView = <Dynamic key={Math.random()} conductor={notifiers.lastUpdated} child={()=>{
            const lastUpdated = data.type === "draft" ? 
                (data.lastUpdated ? parseDateWithTime(data.lastUpdated) : null) :
                (data.lastUpdate ? parseDateWithTime(data.lastUpdate.date) : null);
            return lastUpdated ? <HeaderSubdetail icon="edit">Last updated on <b>{lastUpdated}</b></HeaderSubdetail> : <div />;
        }} />;

        return <header>
            <div className="left">
                <h2>Tourism Attraction Visitor Record</h2>
                <h5>{ `${lguName}, ${province} • ${date} • ${typeLabel}` }</h5>
                <br />
                { controller.type === "submission" ? [
                    <HeaderSubdetail icon="person" key="1">Submitted by <b>{author}</b></HeaderSubdetail>,
                    <HeaderSubdetail icon="input" key="2">Submitted on <b>{parseDateWithTime(data.submittedOn)}</b></HeaderSubdetail>,
                    lastUpdatedView,
                    // approvals.length > 0 ?
                    //     <HeaderSubdetail icon="check_circle" key="3">
                    //         {pluralize(approvals.length, "approval")} {status.approved ? "(including you)" : ""}
                    //     </HeaderSubdetail> : ""
                ] : [ lastUpdatedView ] }
                
                
            </div>
            <div className="buttons">
                { controller.type === "draft" ? [
                    <IconButton key="1" icon="refresh" onClick={() => controller.refresh()} />,
                    <Button key="2" onClick={() => controller.viewSubmissions()}>View Submissions</Button>,
                    <MonthYearSelector key="3" month={data.month} year={data.year} onChange={onDateChange} />, 
                    <Button color="blue" icon="upload" key="5" onClick={() => controller.import()}>IMPORT</Button>,
                    <Button key="6" icon="add" onClick={() => controller.addAttraction()}>ADD ATTRACTION</Button>
                ] : [
                    <Button icon="refresh" key="1" onClick={() => controller.refresh()}>Refresh</Button>,
                    canApprove ? <Dynamic conductor={notifiers.approving} child={() => 
                        <Button key="2" inProgress={status.approving} onClick={() => controller.toggleApprove()}>
                            { status.approving ? "APPROVING" : status.approved ? "UNAPPROVE" : "APPROVE" }
                        </Button>
                    } />  : ""
                ] }
            </div>
        </header>;
    }} />;
}

function HeaderSubdetail({ icon, children }) {
    const iconImage = icons[icon];
    return <div className="subdetail">
        <img src={iconImage} /> {children}
    </div>;
}



function InputCell({ counts, refs, onTotal }) {
    
    const male = counts.male || 0;
    const female = counts.female || 0;
    const ngd = counts.ngd || 0;

    const [ total, setTotal ] = useState(male + female + ngd);

    const onInput = () => {
        const values = refs.map(ref => Number(ref.current.value));
        const newTotal = values.reduce((a, b) => a+b);
        setTotal(newTotal);
        onTotal(newTotal);
    };

    useEffect(() => {
        onTotal(total);
    });

    return <HorizontalGrid gap={8}>
        <FormTableInput reference={refs[0]} format="integer? 0:" defaultValue={male} onChange={onInput} />
        <FormTableInput reference={refs[1]} format="integer? 0:" defaultValue={female} onChange={onInput} />
        <FormTableInput reference={refs[2]} format="integer? 0:" defaultValue={ngd} onChange={onInput} />
        <div style={{ textAlign: "center" }}>{total}</div>
    </HorizontalGrid>;
}


function TitleWithSubtitles({ title, subtitles }) {
    return <div style={{ textAlign: "center" }}>
        {title}<br />
        <HorizontalGrid gap={8}>{subtitles}</HorizontalGrid>
    </div>;
}

function TotalCell({ attrRefs, subcontroller }) {
    // const [ totals, setTotals ] = useState([0, 0, 0]);
    const [ total, setTotal ] = useState([0, 0, 0]);

    subcontroller.onTotal = () => {
        setTotal(attrRefs.reduce((a,b)=> a + (Number(b.current?.value) || 0), 0));
    };

    return <b>{total}</b>;
}

function FormSection({ controller }) {
    const subtitles = [ "MALE", "FEMALE", "NGD", "TOTAL" ];
    // const columns = [
    //     { title: "TOURIST ATTRACTION", width: "31%" },
    //     { title: <TitleWithSubtitles title="FROM THIS PROVINCE" subtitles={subtitles} />, width: "23%" },
    //     { title: <TitleWithSubtitles title="FROM OTHER PROVINCE" subtitles={subtitles} />, width: "23%" },
    //     { title: <TitleWithSubtitles title="FOREIGN ORIGIN" subtitles={subtitles} />, width: "23%" },
    // ];
    const columns = [
        { title: "TOURIST ATTRACTION", width: "250px" },
        { title: <TitleWithSubtitles title="FROM THIS PROVINCE" subtitles={subtitles} />, width: "290px" },
        { title: <TitleWithSubtitles title="FROM OTHER PROVINCE" subtitles={subtitles} />, width: "290px" },
        { title: <TitleWithSubtitles title="FOREIGN ORIGIN" subtitles={subtitles} />, width: "290px" },
        { title: "TOTAL", width: "60px" },
    ];

    const { notifiers, status } = controller;

    return <main>
        <Dynamic conductor={notifiers.reloaded} child={()=>{
            const { data, refs } = controller;
            const { remarks } = data;

            const rows = data.attractions.map((attraction, ai) => {
                const { name, counts: { domestic: { local, external }, foreign } } = attraction;
                const attrRefs = refs[ai].refs;

                const subcontroller = {};

                return [
                    name,
                    <InputCell counts={local} refs={attrRefs.slice(0, 3)} onTotal={() => subcontroller.onTotal()}/>,
                    <InputCell counts={external} refs={attrRefs.slice(3, 6)} onTotal={() => subcontroller.onTotal()}/>,
                    <InputCell counts={foreign} refs={attrRefs.slice(6, 9)} onTotal={() => subcontroller.onTotal()}/>,
                    <TotalCell attrRefs={attrRefs} subcontroller={subcontroller} /> 
                ];
            });

            return <ScrollingTable scrollX={true} columns={columns} rows={rows} footer={controller.type !== "draft" && remarks ? <div className="remarks">*REMARKS: {remarks}</div> : ""} />;
        }} />
        <Dynamic conductor={notifiers.reloading} child={()=>
            <Blocker active={status.reloading}><center><LoadingAnimation />Loading data...</center></Blocker>
        } />
    </main>
}

function Footer({ controller }) {
    const { status, notifiers, type, data: { remarks }} = controller;

    return <Dynamic key={Math.random()} conductor={[notifiers.saving, notifiers.submitting, notifiers.reloading, notifiers.approving, notifiers.deleting, notifiers.publishing]} child={()=>
        <footer>
            {controller.type === "draft" ? <div> 
                <SubmitButtonGroup onSubmit={data => controller.submit(data)} inProgress={status.submitting} />
            </div> : <div>
                { Client.canEditSubmissions ? <Button inProgress={status.deleting} disabled={status.reloading || status.approving || status.saving || status.publishing} onClick={() => controller.delete()} color="red">DELETE</Button> : ""}
                { Client.canPublishSubmissions && status.approved ? <Button inProgress={status.publishing} color="blue" disabled={status.reloading || status.approving || status.saving} onClick={() => controller.publish()}>PUBLISH</Button> : ""}
            </div> }
            <div>
                {(controller.type === "draft" || controller.canEditSubmissions) && !status.approved ? 
                    <Button inProgress={status.saving} disabled={status.reloading || status.approving || status.deleting || status.publishing} onClick={() => controller.save()} color="green" icon="save">{type === "draft" ? (status.saving ? "SAVING" : "SAVE") : (status.saving ? "UPDATING" : "UPDATE")}</Button>
                    : "" }
            </div>
        </footer>
    } />;
}



export default SameDayLGU;